import { FieldGroup, FGSelectInput, FGTextAreaInput } from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import * as Yup from "yup";

import { WorkbookTypeApi, WorkbookTypeDto, WorkbookTypeTranslationDto } from "../../../../api";
import { ERoutes } from "../../../../ERoutes";
import { SmallFormGenerator, FGSRSHCheckboxInput, FGMultiTranslate } from "../../../../components";
import { useApiService, useCrudApi, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { useGlobalData } from "../../../../contexts";
import { useReferential } from "../../../../hooks/useReferential";

export interface IWorkbookTypeDetailPageProps {
  id?: number;
  editMode?: boolean;
}

export const WorkbookTypeDetailPage: React.FunctionComponent<IWorkbookTypeDetailPageProps> = ({ id, editMode }) => {
  const { t } = useTl();
  const api = useApiService(WorkbookTypeApi);
  const history = useHistory();
  const { languages, currentLanguage } = useGlobalData();

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      workbookColorId: Yup.number().nullable().required(t(ETLCodes.Required)),
      hasPedigree: Yup.boolean(),
      workbookTypeTranslations: Yup.array().of(
        Yup.object().shape({
          text: Yup.string().nullable().required(t(ETLCodes.Required)),
        })
      ),
      isSticker: Yup.boolean()
        .nullable()
        .test("has-document", t(ETLCodes.TypeHasNoDocument), async function (value) {
          return value || !!this.parent.documentType;
        }),
    });
  }, [t]);

  const { data, loading, deleteItem, deleting, saveItem, saving } = useCrudApi(
    {
      getApiFn: (getId = id) =>
        getId > 0
          ? api.get(getId)
          : new WorkbookTypeDto({
              isSticker: true,
              workbookTypeTranslations: [
                ...languages.map((l) => new WorkbookTypeTranslationDto({ languageId: l.languageId })),
              ],
            }).toJSON(),
      saveApiFn: (d: WorkbookTypeDto) =>
        d.workbookTypeId > 0 ? api.save(d) : api.save(WorkbookTypeDto.fromJS({ ...d, isSticker: true })),
      onSavedRoute: (d) => `${ERoutes.workbookType}/${d.workbookTypeId}/detail/edit`,
      deleteApiFn: (d) => api.delete(d.workbookTypeId),
      onDeletedRoute: () => ERoutes.workbookType,
    },
    [id]
  );

  const [workbookColor, wbcLoading] = useReferential((a) => a.getWorkbookColors(currentLanguage.languageId), true, [
    currentLanguage,
  ]);
  const [articleIds, articleLoading] = useReferential((a) => a.getArticles(false), true, [currentLanguage]);

  return (
    <SmallFormGenerator
      initialValues={data}
      onSubmit={saveItem}
      editMode={editMode}
      validationSchema={FormSchema}
      loading={loading}
      onCancel={() => history.push(ERoutes.workbookType)}
      onDelete={deleteItem}
      showDeleteButton={id > 0}
      saving={saving}
      deleting={deleting}
    >
      <FieldGroup columns={2}>
        <FieldGroup>
          <FGMultiTranslate
            label={ETLCodes.Description}
            translationsArrayField="workbookTypeTranslations"
            translationField="text"
            translationDto={WorkbookTypeTranslationDto}
          />
        </FieldGroup>
        <FieldGroup>
          <FGSelectInput
            items={workbookColor}
            loading={wbcLoading}
            name="workbookColorId"
            label={t(ETLCodes.WorkbookColor)}
          />
          <FGSelectInput items={articleIds} loading={articleLoading} name="articleId" label={t(ETLCodes.Article)} />
          <FGSRSHCheckboxInput name="hasPedigree" label={t(ETLCodes.HasPedigree)} />
          <FGSRSHCheckboxInput name="canGiveWorkCertificate" label={t(ETLCodes.CanGiveWorkCertificate)} />
          <FGSRSHCheckboxInput name="sociabilityTest" label={t(ETLCodes.HasSocialibilityTest)} />
          <FGSRSHCheckboxInput name="isSticker" label={t(ETLCodes.IsSticker)} visible={id > 0} />
          <FGTextAreaInput name="remark" label={t(ETLCodes.Remark)} />
        </FieldGroup>
      </FieldGroup>
    </SmallFormGenerator>
  );
};
